import { type SVGProps, forwardRef } from 'react';

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'ref'> {
  /** Pixel size — icons are drawn on a 24×24 grid. */
  size?: number | string;
  /** Accessible label — when omitted the icon is marked decorative. */
  title?: string;
  /** Stroke width override; defaults per spec to 1.5 at 24px. */
  strokeWidth?: number;
}

export function createIcon(displayName: string, path: JSX.Element) {
  const Icon = forwardRef<SVGSVGElement, IconProps>(function Icon(
    { size = 24, title, strokeWidth = 1.5, ...rest },
    ref,
  ) {
    const ariaProps = title
      ? { role: 'img' as const, 'aria-label': title }
      : { 'aria-hidden': true, focusable: false as const };
    return (
      <svg
        ref={ref}
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        {...ariaProps}
        {...rest}
      >
        {title ? <title>{title}</title> : null}
        {path}
      </svg>
    );
  });
  Icon.displayName = `Icon${displayName}`;
  return Icon;
}
